/**
 * The wing (M6 spec §6.2; plan P25): the player's wingmen, their command and formation, the size cap and the skill
 * profiles.
 *
 * - **Wingmen** are the player corporation's ships with an Escort(the player) order (`LocalFleet.escortPlayer`) in the
 *   player's system, promoted to real ships by the tier step (`traffic/realShips.ts`, own ships first, D-393). The
 *   subsystem (`subsystem.ts`) recruits them here; tests recruit any ship.
 * - **Size:** the flown hull's stamped `wingSize` (M10 P2d2: the corvette's 5), else `fleet.json wing.size` (3). Storage is
 *   `max(size, corvetteSize)`; `corvetteSize` is only that storage bound now (its rename waits for P4b).
 * - **Effectiveness:** 1 + the flown class's `wingBonus` (the corvette's +10%); the pilot overlay divides aim error and
 *   reaction delay by it (`src/ai/pilot.ts`).
 * - **Skill profiles:** the captain's Combat skill picks M1's rookie, veteran or ace profile (captains.json
 *   `combatProfiles` by level; no captain flies the neutral level's profile).
 * - A recruited wingman flies as an M1 `AIPilot` with the wing's commanded-state overlay (`commands.ts`); a released one
 *   loses the `AIPilot` and goes back to its owner (the traffic pilot).
 *
 * Storage is fixed typed arrays sized at creation; recruiting and releasing are rare, the per-tick paths allocate
 * nothing (D-014). Positions are float64 (D-013).
 */
import { addComponent, entityExists, hasComponent, removeComponent } from 'bitecs'
import { z } from 'zod'
import wingJson from '../data/wing.json'
import fleetJson from '../data/strategic/fleet.json'
import captainsJson from '../data/strategic/captains.json'
import type { AiEventReader } from '../ai/perception'
import type { Sim } from '../sim'
import { captainsConfigSchema, fleetConfigSchema } from '../strategic/schemas'
import { SKILL, SHIP_CLASS_NAMES, type ShipClassName } from '../strategic/types'
import { AIPilot, Control, Life, MAX_ENTITIES, Pos64, Ship } from '../world/components'
import { FORMATION, FORMATION_NAMES, type Formation } from './formation'

/** Wing command codes (spec §6.2; a const object: `erasableSyntaxOnly`). */
export const WING_CMD = { formUp: 0, attack: 1, defend: 2, hold: 3, breakEngage: 4, dock: 5 } as const
export type WingCommandName = keyof typeof WING_CMD
export type WingCommand = (typeof WING_CMD)[WingCommandName]
export const WING_CMD_NAMES = ['formUp', 'attack', 'defend', 'hold', 'breakEngage', 'dock'] as const satisfies readonly WingCommandName[]

const positive = z.number().positive()
const nonNegative = z.number().min(0)

/** src/data/wing.json. */
export const wingTuningSchema = z.strictObject({
  comment: z.string(),
  formation: z.strictObject({ spacingM: positive, backM: nonNegative }),
  slotToleranceM: positive,
  slotGain: positive,
  boostM: positive,
  alignM: positive,
  defendRadiusM: positive,
  attackerMemoryS: positive,
  engageFraction: z.number().gt(0).max(1),
  dockArriveM: positive,
  launchS: nonNegative,
  defaultCommand: z.enum(WING_CMD_NAMES),
  defaultFormation: z.enum(FORMATION_NAMES),
})
export type WingTuning = z.infer<typeof wingTuningSchema>

/** Everything the wing reads: its tuning plus the fleet.json and captains.json parts it uses. */
export interface WingConfig {
  readonly tuning: WingTuning
  /** Wingmen at most, and while flying a corvette. */
  readonly size: number
  readonly corvetteSize: number
  /** Wingmen within this of the player at a relay transit arrive with them. */
  readonly followRelayM: number
  /** The flown class's wing effectiveness bonus (fleet.json classes.*.wingBonus). */
  readonly classBonus: Readonly<Record<ShipClassName, number>>
  /** The M1 profile id per Combat level 1..N (captains.json combatProfiles). */
  readonly combatProfiles: readonly string[]
  /** The level a ship without a captain flies at. */
  readonly neutralLevel: number
}

/** Parses the wing's data (the bundled files by default; tests pass their own). */
export function loadWingConfig(raw: { wing?: unknown; fleet?: unknown; captains?: unknown } = {}): WingConfig {
  const tuning = wingTuningSchema.parse(raw.wing ?? wingJson)
  const fleet = fleetConfigSchema.parse(raw.fleet ?? fleetJson)
  const captains = captainsConfigSchema.parse(raw.captains ?? captainsJson)
  const classBonus = {} as Record<ShipClassName, number>
  for (const c of SHIP_CLASS_NAMES) classBonus[c] = fleet.classes[c]?.wingBonus ?? 0
  return {
    tuning,
    size: fleet.wing.size,
    corvetteSize: fleet.wing.corvetteSize,
    followRelayM: fleet.wing.followRelayM,
    classBonus,
    combatProfiles: captains.combatProfiles,
    neutralLevel: captains.skills.neutralLevel,
  }
}

/** The wing's state. Member `i`'s formation slot is `i`. */
export interface Wing {
  readonly cfg: WingConfig
  /** Storage: the largest cap (`corvetteSize`). */
  readonly max: number
  /** Members now (0..cap). */
  count: number
  /** Member entity ids. */
  readonly eid: Int32Array
  /** Member contact-store slot (the traffic store), −1 when the ship is not a traffic agent (tests). */
  readonly slot: Int32Array
  /** 1 once a member on Dock/return reached its home point (the subsystem then releases it). */
  readonly docked: Uint8Array
  /** Per member: the Hold position point and the Dock/return home point, float64 xyz. */
  readonly hold: Float64Array
  readonly home: Float64Array
  /**
   * Per member: the gameplay radius R (metres) of the station at its home, 0 for a point (a relay, open space). Scale
   * SC-5e2: Dock/return ends `dockArriveM` off the hull (`d − R`), so the wingman arrives at a km-scale station.
   */
  readonly homeR: Float64Array
  /** Per member: its formation slot this tick, float64 world xyz (`updateWing`). */
  readonly slots: Float64Array
  /** Entity id → member index + 1 (0: not a member). */
  readonly index: Int8Array
  /** The current size cap and effectiveness (`setLeader`). */
  cap: number
  effectiveness: number
  command: WingCommand
  formation: Formation
  /** The player's entity, −1 when none. */
  leader: number
  /** The ship the wing attacks or defends against this tick (`updateWing`), −1 = none. */
  focus: number
  /** The player's last attacker and when it hit (sim seconds). */
  attacker: number
  attackerAt: number
  /** Set by a command that recalls returned ships (every command but Dock/return); the subsystem clears it. */
  recall: boolean
  /** The wing's own reader of the combat event ring (created on the first `updateWing` of a sim). */
  reader: AiEventReader | null
}

export function createWing(cfg: WingConfig = loadWingConfig()): Wing {
  const max = Math.max(cfg.size, cfg.corvetteSize)
  return {
    cfg,
    max,
    count: 0,
    eid: new Int32Array(max).fill(-1),
    slot: new Int32Array(max).fill(-1),
    docked: new Uint8Array(max),
    hold: new Float64Array(max * 3),
    home: new Float64Array(max * 3),
    homeR: new Float64Array(max),
    slots: new Float64Array(max * 3),
    index: new Int8Array(MAX_ENTITIES),
    cap: cfg.size,
    effectiveness: 1,
    command: WING_CMD[cfg.tuning.defaultCommand],
    formation: FORMATION[cfg.tuning.defaultFormation],
    leader: -1,
    focus: -1,
    attacker: -1,
    attackerAt: -1e9,
    recall: false,
    reader: null,
  }
}

/**
 * The size cap while flying a hull whose stamped `wingSize` is `wingSize` (M10 P2d2; null: the hull has none, or no
 * hull flies the def): `wingSize`, else `size`. The corvette row's `wingSize: 5` keeps the Mastiff at 5. Every row's
 * `wingSize` stays within the storage cap `max(size, corvetteSize)` (a data test).
 */
export function wingCapFor(cfg: WingConfig, wingSize: number | null): number {
  return wingSize ?? cfg.size
}

/** The wing effectiveness while flying `cls`: 1 + its `wingBonus`. */
export function wingEffectivenessFor(cfg: WingConfig, cls: ShipClassName | null): number {
  return 1 + (cls === null ? 0 : cfg.classBonus[cls])
}

/** The M1 profile id for a captain's skills (by `SKILL`; null or a 0 Combat level: no captain → the neutral level). */
export function combatProfileId(cfg: WingConfig, skills: readonly number[] | null): string {
  const raw = skills === null ? 0 : (skills[SKILL.combat] ?? 0)
  const level = raw >= 1 ? raw : cfg.neutralLevel
  const list = cfg.combatProfiles
  return list[Math.min(Math.max(level, 1), list.length) - 1]
}

/**
 * Sets the leader (the player), the flown class and the flown hull's `wingSize` (M10 P2d2): the cap follows the
 * `wingSize` and the effectiveness the class. Returns how many members are over the new cap (the caller releases the
 * last ones with `releaseWingman`).
 */
export function setLeader(wing: Wing, leader: number, cls: ShipClassName | null, wingSize: number | null): number {
  wing.leader = leader
  wing.cap = wingCapFor(wing.cfg, wingSize)
  wing.effectiveness = wingEffectivenessFor(wing.cfg, cls)
  return wing.count > wing.cap ? wing.count - wing.cap : 0
}

/** True when `eid` is a wingman. */
export function isWingman(wing: Wing, eid: number): boolean {
  return eid >= 0 && eid < wing.index.length && wing.index[eid] > 0
}

/** The member index of `eid`, −1 when it is not a wingman. */
export function memberOf(wing: Wing, eid: number): number {
  return isWingman(wing, eid) ? wing.index[eid] - 1 : -1
}

/**
 * Recruits live ship `eid` (contact slot `slot`, −1 if none) as a wingman flying M1 profile `profile`, with its
 * Dock/return home at (hx, hy, hz), a station of radius `hr` there (0: a point; scale SC-5e2). It gets an `AIPilot` (or keeps the one it has, re-profiled) and flight assist.
 * False when the wing is full, it is already a member or it is dead. Rare.
 */
export function recruitWingman(wing: Wing, sim: Sim, eid: number, slot: number, profile: number, hx: number, hy: number, hz: number, hr = 0): boolean {
  if (wing.count >= wing.cap || isWingman(wing, eid) || eid === wing.leader || Life.alive[eid] !== 1) return false
  if (!hasComponent(sim.world, eid, AIPilot)) {
    addComponent(sim.world, eid, AIPilot)
    AIPilot.state[eid] = 0
    AIPilot.target[eid] = -1
    AIPilot.stateTimer[eid] = 0
    AIPilot.jinkX[eid] = AIPilot.jinkY[eid] = 0
    AIPilot.aimX[eid] = AIPilot.aimY[eid] = AIPilot.aimZ[eid] = 0
    AIPilot.aimVX[eid] = AIPilot.aimVY[eid] = AIPilot.aimVZ[eid] = 0
    AIPilot.sampleTimer[eid] = 0
    AIPilot.lastAttacker[eid] = -1
  }
  AIPilot.profile[eid] = profile >= 0 ? profile : 0
  Ship.faOn[eid] = 1
  const i = wing.count++
  wing.eid[i] = eid
  wing.slot[i] = slot
  wing.docked[i] = 0
  const k = i * 3
  wing.home[k] = hx
  wing.home[k + 1] = hy
  wing.home[k + 2] = hz
  wing.homeR[i] = hr > 0 ? hr : 0
  wing.hold[k] = Pos64.x[eid]
  wing.hold[k + 1] = Pos64.y[eid]
  wing.hold[k + 2] = Pos64.z[eid]
  wing.index[eid] = i + 1
  return true
}

/** Drops member `i` from the roster (the last member takes its slot). No component changes. */
export function dropMember(wing: Wing, i: number): void {
  if (i < 0 || i >= wing.count) return
  const eid = wing.eid[i]
  if (eid >= 0 && eid < wing.index.length) wing.index[eid] = 0
  const last = --wing.count
  if (i !== last) {
    wing.eid[i] = wing.eid[last]
    wing.slot[i] = wing.slot[last]
    wing.docked[i] = wing.docked[last]
    wing.homeR[i] = wing.homeR[last]
    for (let c = 0; c < 3; c++) {
      wing.hold[i * 3 + c] = wing.hold[last * 3 + c]
      wing.home[i * 3 + c] = wing.home[last * 3 + c]
    }
    wing.index[wing.eid[i]] = i + 1
  }
  wing.eid[last] = -1
  wing.slot[last] = -1
  wing.docked[last] = 0
  wing.homeR[last] = 0
}

/**
 * Releases member `i`: it leaves the roster and, when it still lives, loses its `AIPilot` (its owner, the traffic
 * pilot, flies it again) with its controls zeroed. Returns its contact slot (−1 if none). Rare.
 */
export function releaseWingman(wing: Wing, sim: Sim, i: number): number {
  if (i < 0 || i >= wing.count) return -1
  const eid = wing.eid[i]
  const slot = wing.slot[i]
  dropMember(wing, i)
  if (entityExists(sim.world, eid) && hasComponent(sim.world, eid, AIPilot)) {
    removeComponent(sim.world, eid, AIPilot)
    AIPilot.target[eid] = -1 // a crewed battery reads it as the ship's target (M10 P5a7b, review M1)
    Control.surge[eid] = Control.sway[eid] = Control.heave[eid] = 0
    Control.pitch[eid] = Control.yaw[eid] = Control.roll[eid] = 0
    Control.boost[eid] = Control.firePrimary[eid] = Control.fireSecondary[eid] = 0
  }
  return slot
}

/** Writes the member indices within `radiusM` of the leader into `out` (cleared first); the relay follow uses it. */
export function membersNearLeader(wing: Wing, radiusM: number, out: number[]): number[] {
  out.length = 0
  const p = wing.leader
  if (p < 0) return out
  const r2 = radiusM * radiusM
  for (let i = 0; i < wing.count; i++) {
    const e = wing.eid[i]
    if (Life.alive[e] !== 1) continue
    const dx = Pos64.x[e] - Pos64.x[p]
    const dy = Pos64.y[e] - Pos64.y[p]
    const dz = Pos64.z[e] - Pos64.z[p]
    if (dx * dx + dy * dy + dz * dz <= r2) out.push(i)
  }
  return out
}
